import { useState } from 'react';
import { useToast } from '../../../hooks/useToast';
import { Tooltip } from '../../common/Tooltip';
import {
  useCreateScenarioCharacter,
  useDeleteScenarioCharacter,
  useRenameScenarioCharacter,
} from '../../../features/scenarios/hooks';
import type { ScenarioCharactersResult } from '../../../features/scenarios/api';
import { DIALOGUE_LINE_LABEL_MAX_CHARS } from '../../../features/scenarios/types';
import { applyUpdatedScenesToScenes, scenarioCharacters, type ScenarioCharacter } from './telopLines';
import type { Scenario, ScenarioResponse } from './types';
import styles from './ScenarioGenerator.module.css';
import { isMotionV2Script } from '../../../features/scenarios/scriptContract';
import lineStyles from './sceneItem/DialogueLines.module.css';

type ScenarioCharactersSectionProps = {
  scenario: Scenario;
  setLastResult: React.Dispatch<React.SetStateAction<ScenarioResponse | null>>;
};

/** 追加時に FE が払い出す不変キー (snake_case・英小文字始まり。ユーザーには見せない)。 */
export function generateCharacterKey(): string {
  const hex = crypto.randomUUID().replace(/-/g, '').slice(0, 8).toLowerCase();
  return `character_${hex}`;
}

/**
 * シナリオ情報モーダルの「登場人物」区画 (docs/features/scenario-telop-speaker-plan.md §3.7/§3.9)。
 * キャスト俳優以外で一意に識別する人物 (声だけの出演者・集団ラベル等) の一覧と、
 * 追加・改名・削除。改名は参照する dialogue_lines / telop_lines の話者名も BE が一括更新し、
 * 削除は参照行の character_key を外して自由ラベルに戻す (行は消えない)。
 * 更新結果はローカル state (lastResult) へ即時反映する (SceneLocationControl と同じ流儀)。
 */
export function ScenarioCharactersSection({ scenario, setLastResult }: ScenarioCharactersSectionProps) {
  const toast = useToast();
  const createMutation = useCreateScenarioCharacter();
  const renameMutation = useRenameScenarioCharacter();
  const deleteMutation = useDeleteScenarioCharacter();

  const characters = scenarioCharacters(scenario);
  // 声の台本 (motion-v2・§10.7.5): 特徴欄を「声の印象」として案内する (声の割り当てはナレーションセクションで行う)
  const voiceScript = isMotionV2Script(scenario);
  const [mode, setMode] = useState<{ kind: 'view' } | { kind: 'add' } | { kind: 'rename'; key: string }>({ kind: 'view' });
  const [formLabel, setFormLabel] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formVoiceOnly, setFormVoiceOnly] = useState(false);
  const [saving, setSaving] = useState(false);

  const applyResult = (result: ScenarioCharactersResult) => {
    setLastResult((prev) => (prev ? {
      ...prev,
      scenarios: prev.scenarios.map((s: any) => (
        s.id === scenario.id
          ? {
              ...s,
              generation_metadata: { ...(s.generation_metadata ?? {}), characters: result.characters },
              scenes: applyUpdatedScenesToScenes(s.scenes, result.updated_scenes),
            }
          : s
      )),
    } : prev));
  };

  const closeForm = () => {
    setMode({ kind: 'view' });
    setFormLabel('');
    setFormDescription('');
    setFormVoiceOnly(false);
  };

  const startAdd = () => {
    setFormLabel('');
    setFormDescription('');
    setFormVoiceOnly(false);
    setMode({ kind: 'add' });
  };

  const startRename = (c: ScenarioCharacter) => {
    setFormLabel(c.label);
    setFormDescription(c.description ?? '');
    setFormVoiceOnly(c.appears_on_screen === false);
    setMode({ kind: 'rename', key: c.key });
  };

  const handleSave = async () => {
    const label = formLabel.trim();
    if (!label) {
      toast.error('登場人物の名前を入力してください');
      return;
    }
    setSaving(true);
    try {
      const payload = { label, description: formDescription.trim(), appears_on_screen: !formVoiceOnly };
      const result = mode.kind === 'rename'
        ? await renameMutation.mutateAsync({ scenarioId: scenario.id, characterKey: mode.key, payload })
        : await createMutation.mutateAsync({ scenarioId: scenario.id, payload: { ...payload, key: generateCharacterKey() } });
      applyResult(result);
      toast.success(mode.kind === 'rename' ? '登場人物を更新しました' : '登場人物を追加しました');
      closeForm();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || '登場人物の保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c: ScenarioCharacter) => {
    const ok = window.confirm(`登場人物「${c.label}」を削除しますか？\nセリフ・テロップの話者は名前だけが残ります (行は消えません)。`);
    if (!ok) return;
    try {
      const result = await deleteMutation.mutateAsync({ scenarioId: scenario.id, characterKey: c.key });
      applyResult(result);
      toast.success('登場人物を削除しました');
      if (mode.kind === 'rename' && mode.key === c.key) closeForm();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || '登場人物の削除に失敗しました');
    }
  };

  const form = mode.kind !== 'view' && (
    <div data-testid="scenario-character-form" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
      <input
        type="text"
        className={styles.scenarioInfoInput}
        value={formLabel}
        maxLength={DIALOGUE_LINE_LABEL_MAX_CHARS}
        placeholder="名前 (例: インタビュアー)"
        aria-label="登場人物の名前"
        autoFocus
        onChange={(e) => setFormLabel(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.preventDefault(); closeForm(); }
          else if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); void handleSave(); }
        }}
      />
      <input
        type="text"
        className={styles.scenarioInfoInput}
        value={formDescription}
        placeholder={voiceScript ? '声の印象 (任意。例: 落ち着いた女性の声)' : '特徴 (任意。例: 声のみの出演・落ち着いた女性の声)'}
        aria-label="登場人物の特徴"
        onChange={(e) => setFormDescription(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.preventDefault(); closeForm(); }
          else if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); void handleSave(); }
        }}
      />
      <label className="form-checkbox-label" style={{ fontSize: '0.8rem' }}>
        <input type="checkbox" checked={formVoiceOnly} onChange={(e) => setFormVoiceOnly(e.target.checked)} />
        声のみの出演 (映像には登場しない)
      </label>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <button type="button" className="btn-cancel-sm" onClick={closeForm}>キャンセル</button>
        <button type="button" className="btn-modal-sm" onClick={() => void handleSave()} disabled={saving}>
          {mode.kind === 'rename' ? '保存' : '追加'}
        </button>
      </div>
    </div>
  );

  return (
    <div className={styles.scenarioInfoField} data-testid="scenario-characters-section">
      <label className={styles.scenarioInfoLabel}>登場人物 (キャスト以外)</label>
      {characters.length === 0 && mode.kind === 'view' && (
        <span style={{ fontSize: '0.82rem', color: 'rgba(245, 241, 230, 0.5)' }}>未設定</span>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {characters.map((c) => (
          <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }} data-testid={`scenario-character-${c.key}`}>
            <span style={{ fontSize: '0.86rem' }}>{c.label}</span>
            {c.appears_on_screen === false && (
              <span style={{ fontSize: '0.7rem', color: 'rgba(245, 241, 230, 0.55)' }}>声のみ</span>
            )}
            {c.description && (
              <span style={{ fontSize: '0.74rem', color: 'rgba(245, 241, 230, 0.5)' }}>{c.description}</span>
            )}
            <Tooltip content="名前・特徴を編集">
              <button type="button" className={styles.fieldEditButton} onClick={() => startRename(c)} aria-label={`${c.label} を編集`}>
                <img src="/pencil_1.svg" alt="" className={styles.fieldEditIcon} />
              </button>
            </Tooltip>
            <Tooltip content="削除">
              <button type="button" className={styles.fieldEditButton} onClick={() => void handleDelete(c)} aria-label={`${c.label} を削除`}>
                <img src="/delete_2.svg" alt="" className={styles.fieldEditIcon} />
              </button>
            </Tooltip>
            {mode.kind === 'rename' && mode.key === c.key && form}
          </div>
        ))}
      </div>
      {mode.kind === 'add' && form}
      {mode.kind === 'view' && (
        <button type="button" className={lineStyles.addLineButton} onClick={startAdd} style={{ alignSelf: 'flex-start' }}>
          + 登場人物を追加
        </button>
      )}
    </div>
  );
}
